import React, { useEffect, useState } from 'react'
import { navigate, NavigateFn, Router } from '@reach/router'
import Appbar from 'components/Appbar'
import {
  HiddenLocationIndicator,
  IconButton,
  IGridTableCell,
  IGridTableColumn,
  ModalLayer,
  NewGridTable,
  PageContent,
  Popover,
} from '@ui'
import {
  IPortfolioRegion,
  IPortfolioBuilding,
  IBuildingDetails,
  IPortfolioFloor,
  IPortfolioCity,
  IFloorDetails,
} from '@api'
import { useUser } from '@hooks'
import { pluralize } from '@utils'
import usePortfolio, { IPortfolio } from './hooks/usePortfolio'
import Filters from './Filters'
import { PopoverInteractionKind, Position, Classes } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import usePortfolioTooltips, { IPortfolioTooltip } from './hooks/usePortfolioTooltips'
import {
  DepartmentSeats,
  TooltipTrigger,
  AvailableSeats,
  TotalSeats,
  TotalEmployees,
  DepartmentsArea,
  ITooltipTriggerField,
} from './Tooltips'
import { PortfolioDetailTabs } from './Details'
import Notifications from './Notifications'
import { Nav } from '@utils'
import track from '@track'
import classnames from 'classnames'
import useTableState from '@ui/hooks/useTableState'
import { useReload } from '@hooks/reloadContext'
import css from './portfolioDetails.module.scss'
import { useHRLocations } from '@ui/selects/HRLocationSelect'
import { get } from 'lodash'
import LocationFilterNote from 'components/EmployeeManagement/LocationFilterNote'
import { legacyMode } from 'App'

function Skeleton({ withButton }: { withButton?: boolean }) {
  return withButton ? (
    <>
      <div className={Classes.SKELETON}>placeholder</div>
      <div className={`z-1 mt-1 ${Classes.SKELETON}`}>
        <button className="p-1 font-medium text-xs text-grey-600 hover:text-grey-700">
          placeholder
        </button>
      </div>
    </>
  ) : (
    <div className={Classes.SKELETON}>placeholder</div>
  )
}

function isPortfolioBuilding(
  type: IPortfolioFloor | IPortfolioBuilding
): type is IPortfolioBuilding {
  return (type as IPortfolioBuilding).floors !== undefined
}

function isBuildingDetails(
  type: IPortfolioRegion | IPortfolioCity | IBuildingDetails
): type is IBuildingDetails {
  return (type as IBuildingDetails).address !== undefined
}

function isPortfolioRegion(
  item: IPortfolioRegion | IPortfolioCity | IBuildingDetails
): item is IPortfolioRegion {
  return (item as IPortfolioRegion).cities !== undefined
}

function isPortfolioCity(
  item: IPortfolioRegion | IPortfolioCity | IBuildingDetails
): item is IPortfolioCity {
  return (item as IPortfolioCity).buildings !== undefined
}

export default function Portfolio() {
  const hook = usePortfolio()
  const reload = useReload()
  const tooltips = usePortfolioTooltips()
  const tableState = useTableState('portfolioNGridTable')
  const [isOpen, setIsOpen] = useState(true)

  useEffect(() => {
    track.portfolio({
      eventName: track.Event.NAVIGATE,
      props: { pageName: 'Portfolio' },
    })
  }, [])

  const { filteredLocations, treeFiltering, treeExpanding, regions, status } = hook
  const { user } = useUser()

  const units = user?.context?.units === 'sqm' ? 'meters' : 'feet'

  const columns: IGridTableColumn<IPortfolioRegion>[] = [
    {
      label: 'Location',
      Cell: ({ skeleton, cell }) =>
        skeleton ? (
          <Skeleton withButton />
        ) : (
          <div className="flex items-center">
            <div className="ml-2 font-medium text-base hover:underline hover:text-link cursor-pointer">
              {cell?.data.name}
            </div>
          </div>
        ),
      tooltip: (
        <p style={{ maxWidth: 300 }}>
          Locations are a hierarchy of global regions, cities, buildings and floors. Your settings
          in the location filter panel control what locations display here.
        </p>
      ),
    },
    {
      label: 'Total Seats',
      tooltip: (
        <p style={{ maxWidth: 300 }}>
          The physical seat count on the floor. Includes physical seats of all types. Excludes
          neighborhood capacity.
        </p>
      ),
      Cell: () => null,
    },
    {
      label: 'Available Seats',
      Cell: () => null,
      tooltip: (
        <p style={{ maxWidth: 300 }}>
          Total seats minus occupied seats. Because of neighborhoods, shared, and secondary seats
          this can be different from the number of employees at this location.
        </p>
      ),
    },
    {
      label: 'Occupied Seats',
      Cell: () => null,
      tooltip: (
        <p style={{ maxWidth: 300 }}>
          The total number of seats with assigned employees or occupied status. This includes: (a)
          primary and shared seats if a person is assigned to them; (b) all secondary, reserved,
          neighborhood, hoteling, and hot seats.
        </p>
      ),
    },
    {
      label: 'Total Employees',
      Cell: () => null,
      tooltip: (
        <p style={{ maxWidth: 300 }}>
          Number of employees that are seated on the floor. Includes primary, shared, and
          neighborhood seats. Excludes unseated, remote/no desk needed employees.
        </p>
      ),
    },
    {
      label: user?.context?.units,
      Cell: () => null,
      tooltip: (
        <p style={{ maxWidth: 300 }}>
          The area of the floor in square {units} taken from the building/floor information.
        </p>
      ),
    },
    {
      label: 'Density',
      Cell: () => null,
      tooltip: (
        <p style={{ maxWidth: 300 }}>
          Area per seat in square {units}. Calculated by dividing the total square {units} by the
          total number of seats.
        </p>
      ),
    },
    {
      label: 'Occupancy',
      Cell: () => null,
      tooltip: (
        <p style={{ maxWidth: 300 }}>
          The percentage of total seats that are occupied. Total seats minus unoccupied seats,
          divided by total seats. Excludes neighborhood capacity.
        </p>
      ),
    },
    {
      label: 'Actions',
      Cell: () => null,
      tooltip: (
        <p style={{ maxWidth: 300 }}>Hover over a city/building/floor to see available actions.</p>
      ),
    },
  ]

  const filteringLocations = Boolean(filteredLocations.length)

  const formatRegions: (items: IPortfolioRegion[]) => IGridTableCell<IPortfolioRegion>[] = (
    items
  ) => {
    const toFilter = (item: IPortfolioRegion) =>
      filteredLocations.find((l) => (isPortfolioRegion(item) ? item.id === l.id : false)) ||
      treeFiltering[item.id]

    return items
      .filter((i) => (filteringLocations ? toFilter(i) : true))
      .map((r) => {
        const expanded =
          (!filteringLocations && Boolean(tableState.state[r.id]?.expanded)) ||
          (tableState.state[r.id]?.expanded === undefined &&
            Boolean(tableState.state[r.id]?.defaultExpanded))

        return {
          data: r,
          onExpand: (c) => {
            tableState?.setRowState(c.data.id, {
              expanded: !tableState?.state[c.data.id]?.expanded,
            })
          },
          expanded: treeExpanding[r.id] || expanded,
          expandToggle: r.cities.length
            ? `${r.cities.length} ${r.cities.length === 1 ? 'City' : 'Cities'}`
            : undefined,
          child: r.cities.length ? (
            <Cities cities={r.cities} hook={hook} navigate={navigate} tooltips={tooltips} />
          ) : undefined,
        }
      })
  }

  useEffect(() => {
    if (regions.length) {
      tableState.setRowState(regions[0].id, {
        defaultExpanded: true,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions])

  const formatedRegions = formatRegions(regions)
  const locations = useHRLocations()
  const emp = useUser()
  const preferredLocation = get(emp, 'user.employee.preferredLocations', [])
  const locationFilterNote =
    !legacyMode &&
    (preferredLocation.length === 0 || locations.length === preferredLocation.length ? null : (
      <LocationFilterNote />
    ))
  return (
    <>
      <Appbar title="Portfolio" locationFilterNote={locationFilterNote} />
      <PageContent>
        <Filters hook={hook} setIsOpen={setIsOpen} isOpen={isOpen} />
        <NewGridTable<IPortfolioRegion>
          status={status}
          columns={columns}
          data={formatedRegions}
          headerTop={isOpen ? 183 : 55}
        />
      </PageContent>
      <Router primary={false}>
        <ModalLayer path="*" reload={reload.portfolio} />
      </Router>
    </>
  )
}

function reduce(
  loadedBuildings: IPortfolioBuilding[],
  field: keyof Omit<
    IPortfolioBuilding,
    'spaceTypes' | 'spaceUsageTypes' | 'building' | 'floors' | 'departments' | 'notifications'
  >
) {
  return loadedBuildings.reduce(
    (acc: number, b: IPortfolioBuilding | undefined) => acc + (b?.[field] || 0),
    0
  )
}

function Cities({
  cities,
  hook,
  navigate,
  tooltips,
}: {
  cities: IPortfolioCity[]
  hook: IPortfolio
  navigate: NavigateFn
  tooltips: IPortfolioTooltip
}) {
  const { user } = useUser()
  const tableState = useTableState('cities')
  const filteringLocations = Boolean(hook.filteredLocations.length)

  useEffect(() => {
    if (cities.length) {
      tableState.setRowState(cities[0].id, {
        defaultExpanded: true,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cities])

  const getBuildings = (cell: IGridTableCell<IPortfolioCity> | undefined) =>
    cell
      ? cell.data.buildings.reduce(
          (acc, b) => [...acc, hook.buildings[b.id]],
          [] as IPortfolioBuilding[]
        )
      : []

  const citiesColumns: IGridTableColumn<IPortfolioCity>[] = [
    {
      label: 'City Name',
      Cell: ({ cell }) => (
        <div data-test="city_name" className="flex items-center">
          <div className="ml-2 font-medium text-base">{cell?.data.name}</div>
        </div>
      ),
    },
    {
      label: 'Total Seats',
      Cell: ({ cell }) => {
        const loadedBuildings = getBuildings(cell)
        const workstations = reduce(loadedBuildings, 'workstations')

        return (
          <div data-test="total_seats">{loadedBuildings.every(Boolean) ? workstations : null}</div>
        )
      },
    },
    {
      label: 'Available Seats',
      Cell: ({ cell }) => {
        const loadedBuildings = getBuildings(cell)
        const unoccupied = reduce(loadedBuildings, 'unoccupied')

        return (
          <div data-test="available_seats">
            {loadedBuildings.every(Boolean) ? unoccupied : null}
          </div>
        )
      },
    },
    {
      label: 'Occupied Seats',
      Cell: ({ cell }) => {
        const loadedBuildings = getBuildings(cell)
        const occupied = reduce(loadedBuildings, 'occupied')

        return (
          <div data-test="occupied_seats">{loadedBuildings.every(Boolean) ? occupied : null}</div>
        )
      },
    },
    {
      label: 'Total Employees',
      Cell: ({ cell }) => {
        const loadedBuildings = getBuildings(cell)
        const seatedCount = reduce(loadedBuildings, 'seatedCount')

        return (
          <div data-test="total_employees">
            {loadedBuildings.every(Boolean) ? seatedCount : null}
          </div>
        )
      },
    },
    {
      label: user?.context?.units,
      Cell: ({ cell }) => {
        const loadedBuildings = getBuildings(cell)
        const area = reduce(
          loadedBuildings,
          user?.context?.units === 'sqm' ? 'areaSqrm' : 'areaSqft'
        )

        return <div data-test="area_units">{loadedBuildings.every(Boolean) ? area : null}</div>
      },
    },
    {
      label: 'Density',
      Cell: ({ cell }) => {
        const loadedBuildings = getBuildings(cell)
        const density = reduce(
          loadedBuildings,
          user?.context?.units === 'sqm' ? 'densitySqrm' : 'densitySqft'
        )

        return <div data-test="density">{loadedBuildings.every(Boolean) ? density : null}</div>
      },
    },
    {
      label: 'Occupancy',
      Cell: ({ cell }) => {
        const loadedBuildings = getBuildings(cell)
        const occupancy = reduce(loadedBuildings, 'occupied')
        const workstations = reduce(loadedBuildings, 'workstations')

        loadedBuildings.every(Boolean)

        return (
          <div data-test="occupancy" className="w-full items-center">
            <NewGridTable.PercentBar
              value={
                loadedBuildings.every(Boolean)
                  ? loadedBuildings.length === 1
                    ? loadedBuildings[0].occupancy || 0
                    : workstations === 0
                    ? 0
                    : Math.round((occupancy / workstations) * 100)
                  : 0
              }
              normal={hook.normalOccupancy || 0}
              alert={hook.alertOccupancy || 0}
            />
          </div>
        )
      },
    },
    {
      label: 'Actions',
      Cell: ({ cell, isHighlighted }) => {
        const links = (
          <div className="f mha">
            {hook.projectionsEnabled && (
              <SmallIconButton
                iconName="chart-line"
                tooltip="Go to Projections"
                data-test="go_to_projections"
                onClick={() => Nav.projections({ city: cell?.data.name })}
              />
            )}
            {hook.stackingEnabled && (
              <SmallIconButton
                iconName="layer-plus"
                tooltip="Go to Stack"
                data-test="go_to_stack"
                onClick={() =>
                  Nav.stack({
                    planId: hook.assignmentPlanId,
                    city: cell?.data.name,
                  })
                }
              />
            )}
          </div>
        )

        return cell && isHighlighted ? links : null
      },
    },
  ]

  const formatCity: (items: IPortfolioCity[]) => IGridTableCell<IPortfolioCity>[] = (
    items: IPortfolioCity[]
  ) => {
    const toFilter = (city: IPortfolioCity) =>
      hook.filteredLocations.find(
        (l) =>
          (isPortfolioCity(l)
            ? city.id === l.id
            : isPortfolioRegion(l)
            ? l.cities.find((c) => c.id === city.id)
            : false) || hook.treeFiltering[city.id]
      )

    return items
      .filter((city) => {
        return filteringLocations ? toFilter(city) : true
      })
      .map((c) => {
        const expanded =
          (!filteringLocations && Boolean(tableState.state[c.id]?.expanded)) ||
          (tableState.state[c.id]?.expanded === undefined &&
            Boolean(tableState.state[c.id]?.defaultExpanded))

        return {
          data: c,
          onExpand: (c) => {
            tableState?.setRowState(c.data.id, {
              expanded: !tableState?.state[c.data.id]?.expanded,
            })
          },
          onExpandCallback: (c) => {
            hook.loadCities({
              cities: [c.data.name],
              assignmentPlanId: hook.assignmentPlanId,
              includeOldBuildings: hook.showInactiveBuildings,
            })
          },
          expandToggle: c.buildings.length
            ? `${c.buildings.length} ${pluralize(c.buildings.length, 'Building')}`
            : undefined,
          expanded: hook.treeExpanding[c.id] || expanded,
          child: c.buildings.length ? (
            <Buildings
              city={c.name}
              buildings={c.buildings}
              navigate={navigate}
              hook={hook}
              tooltips={tooltips}
            />
          ) : undefined,
        }
      })
  }

  const formatedCities = formatCity(cities)

  return (
    <NewGridTable<IPortfolioCity>
      status="idle"
      sticky={false}
      minCellWidth="auto"
      data={formatedCities}
      columns={citiesColumns}
      noDataMessage="No Cities"
      childTable={true}
    />
  )
}

function link({
  buildingId,
  floorId,
  tab,
  apId,
}: {
  buildingId: string
  apId: string
  floorId?: string
  tab?: PortfolioDetailTabs
}) {
  Nav.portfolio({
    buildingId,
    floorId,
    tab,
    apId,
  })
}

function Buildings({
  city,
  buildings,
  navigate,
  hook,
  tooltips,
}: {
  city: string
  buildings: IBuildingDetails[]
  navigate?: NavigateFn
  hook: IPortfolio
  tooltips: IPortfolioTooltip
}) {
  const { user } = useUser()
  const tableState = useTableState(`portfolio-buildings-${city.split(' ').join('-')}`)
  const filteringLocations = Boolean(hook.filteredLocations.length)

  const getBuilding: (
    cell: IGridTableCell<IBuildingDetails> | undefined
  ) => IPortfolioBuilding | undefined = (cell) => (cell ? hook.buildings[cell.data.id] : undefined)

  const buildingsColumns: IGridTableColumn<IBuildingDetails>[] = [
    {
      label: 'Building Name',
      Cell: ({ cell }) => {
        const loadedBuilding = getBuilding(cell)

        return (
          <div data-test="building_name" className="flex items-center">
            <div
              className="ml-2 font-medium text-base hover:underline hover:text-link cursor-pointer"
              onClick={() => {
                if (cell)
                  link({
                    buildingId: cell.data.id,
                    apId: hook.assignmentPlanId,
                  })
              }}>
              {cell?.data.name}
            </div>
            {!cell?.data.floors?.find((f) => !f?.hidden) && (
              <span className="ml2">
                <HiddenLocationIndicator />
              </span>
            )}
            {loadedBuilding && Boolean(loadedBuilding?.building.notifications?.length) && (
              <span className="ml2">
                <Notifications
                  notifications={loadedBuilding.building.notifications}
                  hook={hook}
                  buildingId={loadedBuilding.building.id}
                />
              </span>
            )}
          </div>
        )
      },
    },
    {
      label: 'Total Seats',
      Cell: ({ cell }) => {
        const loadedBuilding = getBuilding(cell)

        return (
          <div
            data-test="total_seats_tooltip"
            className={classnames({ [Classes.SKELETON]: !loadedBuilding })}>
            {loadedBuilding ? (
              <TooltipCell
                data={loadedBuilding}
                field="workstations"
                tooltips={tooltips}
                assignmentPlanId={hook.assignmentPlanId}
              />
            ) : (
              'placeholder'
            )}
          </div>
        )
      },
    },
    {
      label: 'Available Seats',
      Cell: ({ cell }) => {
        const loadedBuilding = getBuilding(cell)

        return (
          <div
            data-test="available_seats_tooltip"
            className={classnames({ [Classes.SKELETON]: !loadedBuilding })}>
            {loadedBuilding ? (
              <TooltipCell
                data={loadedBuilding}
                field="unoccupied"
                tooltips={tooltips}
                assignmentPlanId={hook.assignmentPlanId}
              />
            ) : (
              'placehldr'
            )}
          </div>
        )
      },
    },
    {
      label: 'Occupied Seats',
      Cell: ({ cell }) => {
        const loadedBuilding = getBuilding(cell)

        return (
          <div
            data-test="occupied_seats_tooltip"
            className={classnames({ [Classes.SKELETON]: !loadedBuilding })}>
            {loadedBuilding ? (
              <TooltipCell
                data={loadedBuilding}
                field="occupied"
                tooltips={tooltips}
                assignmentPlanId={hook.assignmentPlanId}
              />
            ) : (
              'placehldr'
            )}
          </div>
        )
      },
    },
    {
      label: 'Total Employees',
      Cell: ({ cell }) => {
        const loadedBuilding = getBuilding(cell)

        return (
          <div
            data-test="total_employees_tooltip"
            className={classnames({ [Classes.SKELETON]: !loadedBuilding })}>
            {loadedBuilding ? (
              <TooltipCell
                data={loadedBuilding}
                field="seatedCount"
                tooltips={tooltips}
                assignmentPlanId={hook.assignmentPlanId}
              />
            ) : (
              'placehldr'
            )}
          </div>
        )
      },
    },
    {
      label: user?.context?.units,
      Cell: ({ cell }) => {
        const loadedBuilding = getBuilding(cell)
        const value: keyof Pick<IPortfolioBuilding, 'areaSqrm' | 'areaSqft'> =
          user?.context?.units === 'sqm' ? 'areaSqrm' : 'areaSqft'

        return (
          <div
            data-test="unit_tooltip"
            className={classnames({ [Classes.SKELETON]: !loadedBuilding })}>
            {loadedBuilding ? (
              <TooltipCell
                data={loadedBuilding}
                field={value}
                tooltips={tooltips}
                assignmentPlanId={hook.assignmentPlanId}
              />
            ) : (
              'placehldr'
            )}
          </div>
        )
      },
    },
    {
      label: 'Density',
      Cell: ({ cell }) => {
        const loadedBuilding = getBuilding(cell)
        const value: keyof Pick<IPortfolioBuilding, 'densitySqrm' | 'densitySqft'> =
          user?.context?.units === 'sqm' ? 'densitySqrm' : 'densitySqft'

        return (
          <div
            data-test="density_tooltip"
            className={classnames({ [Classes.SKELETON]: !loadedBuilding })}>
            {loadedBuilding ? (
              <TooltipCell
                data={loadedBuilding}
                field={value}
                tooltips={tooltips}
                assignmentPlanId={hook.assignmentPlanId}
              />
            ) : (
              'placehldr'
            )}
          </div>
        )
      },
    },
    {
      label: 'Occupancy',
      Cell: ({ cell }) => {
        const loadedBuilding = getBuilding(cell)

        return (
          <div data-test="occupancy_tooltip" className="w-full items-center">
            <NewGridTable.PercentBar
              value={loadedBuilding ? loadedBuilding.occupancy : 0}
              normal={hook.normalOccupancy || 0}
              alert={hook.alertOccupancy || 0}
            />
          </div>
        )
      },
    },
    {
      label: 'Actions',
      Cell: ({ cell, isHighlighted }) => {
        const links = (
          <div className="f mha">
            {hook.stackingEnabled && (
              <SmallIconButton
                iconName="layer-plus"
                tooltip="Go to Stack"
                onClick={() => {
                  if (cell)
                    Nav.stack({
                      planId: hook.assignmentPlanId,
                      city: cell.data.city,
                    })
                }}
              />
            )}
            <SmallIconButton
              iconName="pen"
              onClick={() => {
                if (cell)
                  Nav.quickBuilding({
                    buildingId: cell.data.id,
                  })
              }}
              tooltip="Manage building"
            />
          </div>
        )

        return cell && isHighlighted ? links : null
      },
    },
  ]

  const formatBuilding: (items: IBuildingDetails[]) => IGridTableCell<IBuildingDetails>[] = (
    items
  ) => {
    const toFilter = (building: IBuildingDetails) =>
      hook.filteredLocations.find(
        (l) =>
          (isBuildingDetails(l)
            ? building.id === l.id
            : isPortfolioCity(l)
            ? l.buildings.find((b) => b.id === building.id)
            : false) || Boolean(hook.treeFiltering[building.id])
      )

    return items
      .filter((building) => (filteringLocations ? toFilter(building) : true))
      .map((b) => {
        const expanded = !filteringLocations && Boolean(tableState.state[b.id]?.expanded)

        return {
          data: b,
          expanded: hook.treeExpanding[b.id] || expanded,
          expandToggle: b.floors?.length
            ? `${b.floors.length} ${pluralize(b.floors.length, 'Floor')}`
            : undefined,
          onExpand: (c) => {
            tableState?.setRowState(c.data.id, {
              expanded: !tableState?.state[c.data.id]?.expanded,
            })
          },
          child: b.floors?.length ? (
            <Floors
              floors={b.floors?.map((f) => ({
                data: f,
              }))}
              navigate={navigate}
              hook={hook}
              tooltips={tooltips}
            />
          ) : undefined,
        }
      })
  }

  const formatedBuildings = formatBuilding(buildings)

  return (
    <NewGridTable<IBuildingDetails>
      status="idle"
      sticky={false}
      minCellWidth="auto"
      data={formatedBuildings}
      columns={buildingsColumns}
      noDataMessage="No Buildings"
      childTable={true}
    />
  )
}

function TooltipCell({
  data,
  field: f,
  tooltips,
  assignmentPlanId,
}: {
  data: IPortfolioFloor | IPortfolioBuilding
  field: ITooltipTriggerField
  tooltips: IPortfolioTooltip
  assignmentPlanId: string
}) {
  const { user } = useUser()

  const units = user?.context?.units
  const building: { id: string } = isPortfolioBuilding(data) ? data.building : data.floor.building

  const isBuilding = isPortfolioBuilding(data)

  const floor: { id: string; building: { id: string } } | undefined = isPortfolioBuilding(data)
    ? undefined
    : data.floor

  const tooltip =
    tooltips[isBuilding ? 'buildings' : 'floors'][
      isPortfolioBuilding(data) ? building.id : data.floor.id
    ]

  return (
    <Popover
      usePortal={true}
      modifiers={{
        arrow: { enabled: true },
        flip: { enabled: true },
      }}
      disabled={f === 'densitySqft' || f === 'densitySqrm'}
      onOpening={() => tooltips.getBuilding(building.id, assignmentPlanId)}
      content={
        <TooltipTrigger
          field={f}
          totalSeats={
            <TotalSeats
              data={tooltip?.byUsageType}
              units={units}
              link={() =>
                link({
                  buildingId: building.id,
                  floorId: floor?.id,
                  tab: PortfolioDetailTabs.sbut,
                  apId: assignmentPlanId,
                })
              }
            />
          }
          availableSeats={
            <AvailableSeats
              unallocated={data.unallocated}
              allocated={data.allocated}
              data={tooltip?.byUnoccupiedInDepartments}
              link={() =>
                link({
                  buildingId: building.id,
                  floorId: floor?.id,
                  tab: PortfolioDetailTabs.sbd,
                  apId: assignmentPlanId,
                })
              }
            />
          }
          departmentSeats={
            <DepartmentSeats
              data={tooltip?.byAllocatedInDepartments}
              link={() =>
                link({
                  buildingId: building.id,
                  floorId: floor?.id,
                  tab: PortfolioDetailTabs.sbd,
                  apId: assignmentPlanId,
                })
              }
            />
          }
          totalEmployees={
            <TotalEmployees
              data={tooltip?.byEmployeesInDepartment}
              link={() =>
                link({
                  buildingId: building.id,
                  floorId: floor?.id,
                  tab: PortfolioDetailTabs.sebd,
                  apId: assignmentPlanId,
                })
              }
            />
          }
          departmentsArea={
            <DepartmentsArea
              units={units}
              data={tooltip?.byDepartmentsArea}
              link={() =>
                link({
                  buildingId: building.id,
                  floorId: floor?.id,
                  tab: PortfolioDetailTabs.abd,
                  apId: assignmentPlanId,
                })
              }
            />
          }
        />
      }
      interactionKind={PopoverInteractionKind.HOVER}>
      <div className={css.hovered}>
        <NewGridTable.Number value={data[f]} />
      </div>
    </Popover>
  )
}

function Floors({
  floors,
  navigate,
  hook,
  tooltips,
}: {
  floors: IGridTableCell<IFloorDetails>[]
  navigate?: NavigateFn
  hook: IPortfolio
  tooltips: IPortfolioTooltip
}) {
  const { user } = useUser()

  const getFloor: (
    cell: IGridTableCell<IFloorDetails> | undefined
  ) => IPortfolioFloor | undefined = (cell) => {
    return cell
      ? hook.buildings?.[cell.data.building.id]?.floors.find((f) => f.floor.id === cell.data.id)
      : undefined
  }

  const floorsColumns: IGridTableColumn<IFloorDetails>[] = [
    {
      label: 'Floor Name',
      Cell: ({ cell }) => {
        const loadedFloor = getFloor(cell)

        return (
          <div className="flex items-center">
            <div
              className="ml-2 font-medium text-base hover:underline hover:text-link cursor-pointer"
              onClick={() => {
                if (cell)
                  link({
                    buildingId: cell.data.building.id,
                    floorId: cell.data.id,
                    apId: hook.assignmentPlanId,
                  })
              }}>
              {cell?.data.name}
            </div>
            {loadedFloor && Boolean(loadedFloor.floor.notifications.length) && (
              <span className="ml2">
                <Notifications
                  notifications={loadedFloor.floor.notifications}
                  hook={hook}
                  buildingId={loadedFloor.floor.building.id}
                />
              </span>
            )}
          </div>
        )
      },
    },
    {
      label: 'Total Seats',
      Cell: ({ cell }) => {
        const loadedFloor = getFloor(cell)

        return (
          <div className={classnames({ [Classes.SKELETON]: !loadedFloor })}>
            {loadedFloor ? (
              <TooltipCell
                data={loadedFloor}
                field="workstations"
                tooltips={tooltips}
                assignmentPlanId={hook.assignmentPlanId}
              />
            ) : (
              'placeholder'
            )}
          </div>
        )
      },
    },
    {
      label: 'Available Seats',
      Cell: ({ cell }) => {
        const loadedFloor = getFloor(cell)

        return (
          <div className={classnames({ [Classes.SKELETON]: !loadedFloor })}>
            {loadedFloor ? (
              <TooltipCell
                data={loadedFloor}
                field="unoccupied"
                tooltips={tooltips}
                assignmentPlanId={hook.assignmentPlanId}
              />
            ) : (
              'placeholder'
            )}
          </div>
        )
      },
    },
    {
      label: 'Occupied Seats',
      Cell: ({ cell }) => {
        const loadedFloor = getFloor(cell)

        return (
          <div className={classnames({ [Classes.SKELETON]: !loadedFloor })}>
            {loadedFloor ? (
              <TooltipCell
                data={loadedFloor}
                field="occupied"
                tooltips={tooltips}
                assignmentPlanId={hook.assignmentPlanId}
              />
            ) : (
              'placeholder'
            )}
          </div>
        )
      },
    },
    {
      label: 'Total Employees',
      Cell: ({ cell }) => {
        const loadedFloor = getFloor(cell)

        return (
          <div className={classnames({ [Classes.SKELETON]: !loadedFloor })}>
            {loadedFloor ? (
              <TooltipCell
                data={loadedFloor}
                field="seatedCount"
                tooltips={tooltips}
                assignmentPlanId={hook.assignmentPlanId}
              />
            ) : (
              'placeholder'
            )}
          </div>
        )
      },
    },
    {
      label: user?.context?.units,
      Cell: ({ cell }) => {
        const loadedFloor = getFloor(cell)
        const value: keyof Pick<IPortfolioFloor, 'areaSqrm' | 'areaSqft'> =
          user?.context?.units === 'sqm' ? 'areaSqrm' : 'areaSqft'

        return (
          <div className={classnames({ [Classes.SKELETON]: !loadedFloor })}>
            {loadedFloor ? (
              <TooltipCell
                data={loadedFloor}
                field={value}
                tooltips={tooltips}
                assignmentPlanId={hook.assignmentPlanId}
              />
            ) : (
              'placehldr'
            )}
          </div>
        )
      },
    },
    {
      label: 'Density',
      Cell: ({ cell }) => {
        const loadedFloor = getFloor(cell)
        const value: keyof Pick<IPortfolioFloor, 'densitySqrm' | 'densitySqft'> =
          user?.context?.units === 'sqm' ? 'densitySqrm' : 'densitySqft'

        return (
          <div className={classnames({ [Classes.SKELETON]: !loadedFloor })}>
            {loadedFloor ? (
              <TooltipCell
                data={loadedFloor}
                field={value}
                tooltips={tooltips}
                assignmentPlanId={hook.assignmentPlanId}
              />
            ) : (
              'placehldr'
            )}
          </div>
        )
      },
    },
    {
      label: 'Occupancy',
      Cell: ({ cell }) => {
        const loadedFloor = getFloor(cell)

        return (
          <div className="w-full items-center">
            <NewGridTable.PercentBar
              value={loadedFloor ? loadedFloor.occupancy : 0}
              normal={hook.normalOccupancy || 0}
              alert={hook.alertOccupancy || 0}
            />
          </div>
        )
      },
    },
    {
      label: 'Actions',
      Cell: ({ cell, isHighlighted }) => {
        const links = (
          <div className="f mha">
            <SmallIconButton
              iconName="map-marked"
              onClick={() => {
                if (cell)
                  Nav.floorPlan({
                    floorId: cell.data.id,
                    planId: hook.assignmentPlanId,
                  })
              }}
              tooltip="Go to Floor Plan"
            />
            {hook.stackingEnabled && (
              <SmallIconButton
                iconName="layer-plus"
                tooltip="Go to Stack"
                onClick={() => {
                  if (cell)
                    Nav.stack({
                      planId: hook.assignmentPlanId,
                      city: cell.data.building.id,
                    })
                }}
              />
            )}
            <SmallIconButton
              tooltip="Manage floor"
              iconName="pen"
              onClick={() => {
                if (cell) Nav.quickFloor({ floorId: cell.data.id })
              }}
            />
          </div>
        )

        return cell && isHighlighted ? links : null
      },
    },
  ]

  return (
    <NewGridTable<IFloorDetails>
      status="idle"
      sticky={false}
      data={floors}
      columns={floorsColumns}
      noDataMessage="No Floors"
      childTable={true}
    />
  )
}

const SmallIconButton = ({ tooltip, ...props }: any) => {
  const button = (
    <IconButton
      {...{
        ...props,
        round: true,
        minimal: true,
        style: { marginTop: -7 },
      }}
    />
  )
  if (!tooltip) return button
  return (
    <Tooltip2 content={tooltip} position={Position.TOP}>
      {button}
    </Tooltip2>
  )
}
